/**
 * Reports Page
 * Comprehensive project reports with export options
 */

import { Card } from '@/components/common/Card';
import { Button } from '@fluentui/react-components';
import {
  ArrowDownload24Regular,
  DocumentPdf24Regular,
  DocumentTable24Regular,
} from '@fluentui/react-icons';
import { mockCalculationResults, mockProject, mockTenants } from '@/services/mockData';
import { formatCurrency, formatPercentage } from '@/utils/formatters';
import { AIOverview } from '@/components/reports/AIOverview';

export function Reports() {
  const handleExport = (format: string) => {
    alert(`Exporting to ${format}... (Mock - not implemented)`);
  };

  // Prepare data for AI Overview
  const projectData = {
    name: mockProject.name,
    location: `${mockProject.city}, ${mockProject.state}`,
    leveredIRR: mockCalculationResults.returnMetrics.leveredIRR,
    equityMultiple: mockCalculationResults.returnMetrics.equityMultiple,
    totalCost: mockCalculationResults.totalProjectCost,
    stabilizedNOI: mockCalculationResults.stabilizedNOI,
    yieldOnCost: mockCalculationResults.yieldOnCost,
    exitCapRate: mockCalculationResults.noiCapAtStabilization,
  };

  const tenantData = {
    totalTenants: mockTenants.length,
    totalNRA: mockTenants.reduce((sum, t) => sum + t.nra, 0),
    categories: [...new Set(mockTenants.map((t) => t.category))],
  };

  return (
    <div className="reports p-6">
      <header className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold mb-2">Reports</h1>
          <p className="text-gray-600">Comprehensive project analysis and exports</p>
        </div>
        <div className="flex gap-3">
          <Button icon={<DocumentTable24Regular />} onClick={() => handleExport('Excel')}>
            Export to Excel
          </Button>
          <Button icon={<DocumentPdf24Regular />} onClick={() => handleExport('PDF')}>
            Export to PDF
          </Button>
          <Button icon={<ArrowDownload24Regular />} onClick={() => handleExport('CSV')}>
            Download CSV
          </Button>
        </div>
      </header>

      {/* AI Overview */}
      <AIOverview projectData={projectData} tenantData={tenantData} />

      {/* Executive Summary */}
      <Card className="p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">Executive Summary</h2>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-6">
          <div>
            <div className="text-sm text-gray-600 mb-1">Project</div>
            <div className="font-semibold">{mockProject.name}</div>
          </div>
          <div>
            <div className="text-sm text-gray-600 mb-1">Location</div>
            <div className="font-semibold">{mockProject.city}, {mockProject.state}</div>
          </div>
          <div>
            <div className="text-sm text-gray-600 mb-1">Total NRA</div>
            <div className="font-semibold">{mockProject.total_nra?.toLocaleString()} SF</div>
          </div>
          <div>
            <div className="text-sm text-gray-600 mb-1">Type</div>
            <div className="font-semibold">{mockProject.retail_type}</div>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div className="p-4 bg-blue-50 rounded-lg">
            <div className="text-sm text-gray-600 mb-1">Levered IRR</div>
            <div className="text-2xl font-bold">{formatPercentage(mockCalculationResults.returnMetrics.leveredIRR)}</div>
          </div>
          <div className="p-4 bg-green-50 rounded-lg">
            <div className="text-sm text-gray-600 mb-1">Equity Multiple</div>
            <div className="text-2xl font-bold">{mockCalculationResults.returnMetrics.equityMultiple.toFixed(2)}x</div>
          </div>
          <div className="p-4 bg-purple-50 rounded-lg">
            <div className="text-sm text-gray-600 mb-1">Development Spread</div>
            <div className="text-2xl font-bold">{formatCurrency(mockCalculationResults.developmentSpread)}</div>
          </div>
          <div className="p-4 bg-orange-50 rounded-lg">
            <div className="text-sm text-gray-600 mb-1">Yield on Cost</div>
            <div className="text-2xl font-bold">{formatPercentage(mockCalculationResults.yieldOnCost)}</div>
          </div>
        </div>
      </Card>

      {/* Financial Summary */}
      <Card className="p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">Financial Summary</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div>
            <h3 className="font-semibold mb-3">Investment Summary</h3>
            <table className="w-full text-sm">
              <tbody className="divide-y">
                <tr><td className="py-2">Total Project Cost</td><td className="text-right font-semibold">{formatCurrency(mockCalculationResults.totalProjectCost)}</td></tr>
                <tr><td className="py-2">Total Equity Invested</td><td className="text-right">{formatCurrency(mockCalculationResults.totalEquityInvested)}</td></tr>
                <tr><td className="py-2">Total Debt Financing</td><td className="text-right">{formatCurrency(mockCalculationResults.totalDebtFinancing)}</td></tr>
                <tr><td className="py-2">Stabilized NOI</td><td className="text-right font-semibold">{formatCurrency(mockCalculationResults.stabilizedNOI)}</td></tr>
              </tbody>
            </table>
          </div>

          <div>
            <h3 className="font-semibold mb-3">Return Metrics</h3>
            <table className="w-full text-sm">
              <tbody className="divide-y">
                <tr><td className="py-2">Levered IRR</td><td className="text-right font-semibold">{formatPercentage(mockCalculationResults.returnMetrics.leveredIRR)}</td></tr>
                <tr><td className="py-2">Unlevered IRR</td><td className="text-right">{formatPercentage(mockCalculationResults.returnMetrics.unleveredIRR)}</td></tr>
                <tr><td className="py-2">Equity Multiple</td><td className="text-right">{mockCalculationResults.returnMetrics.equityMultiple.toFixed(2)}x</td></tr>
                <tr><td className="py-2">Avg Cash-on-Cash</td><td className="text-right">{formatPercentage(mockCalculationResults.returnMetrics.averageCashOnCash)}</td></tr>
              </tbody>
            </table>
          </div>
        </div>
      </Card>

      {/* Annual Cash Flow Table */}
      <Card className="p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">Annual Cash Flow Summary</h2>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left">Year</th>
                <th className="px-4 py-2 text-right">Revenue</th>
                <th className="px-4 py-2 text-right">Expenses</th>
                <th className="px-4 py-2 text-right">NOI</th>
                <th className="px-4 py-2 text-right">Debt Service</th>
                <th className="px-4 py-2 text-right">Cash Flow</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {mockCalculationResults.annualCashFlows.map((cf) => (
                <tr key={cf.year}>
                  <td className="px-4 py-2">Year {cf.year}</td>
                  <td className="px-4 py-2 text-right">{formatCurrency(cf.revenue)}</td>
                  <td className="px-4 py-2 text-right">{formatCurrency(cf.expenses)}</td>
                  <td className="px-4 py-2 text-right font-semibold">{formatCurrency(cf.noi)}</td>
                  <td className="px-4 py-2 text-right">{formatCurrency(cf.debtService)}</td>
                  <td className="px-4 py-2 text-right font-semibold">{formatCurrency(cf.cashFlow)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Reversion Analysis */}
      <Card className="p-6">
        <h2 className="text-xl font-semibold mb-4">Reversion Analysis (Year 5)</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div>
            <table className="w-full text-sm">
              <tbody className="divide-y">
                <tr><td className="py-2">Stabilized NOI</td><td className="text-right font-semibold">{formatCurrency(mockCalculationResults.reversion.stabilizedNOI)}</td></tr>
                <tr><td className="py-2">Exit Cap Rate</td><td className="text-right">{formatPercentage(mockCalculationResults.reversion.exitCapRate)}</td></tr>
                <tr><td className="py-2">Gross Sale Price</td><td className="text-right font-semibold">{formatCurrency(mockCalculationResults.reversion.grossSalePrice)}</td></tr>
              </tbody>
            </table>
          </div>
          <div>
            <table className="w-full text-sm">
              <tbody className="divide-y">
                <tr><td className="py-2">Selling Costs</td><td className="text-right">{formatCurrency(mockCalculationResults.reversion.sellingCosts)}</td></tr>
                <tr><td className="py-2">Broker Commission</td><td className="text-right">{formatCurrency(mockCalculationResults.reversion.brokerCommission)}</td></tr>
                <tr><td className="py-2">Loan Payoff</td><td className="text-right">{formatCurrency(mockCalculationResults.reversion.loanPayoff)}</td></tr>
                <tr><td className="py-2 font-bold">Net Proceeds to Equity</td><td className="text-right font-bold">{formatCurrency(mockCalculationResults.reversion.netProceedsToEquity)}</td></tr>
              </tbody>
            </table>
          </div>
        </div>
      </Card>
    </div>
  );
}
